import React, { useEffect, useState } from 'react';
import { Label } from '../../../../UiKit';
import EditDDModal from './EditDDModal';
import RemoveDDModal from './RemoveDDModal';
import {
  BISTRO_ORG_ID,
  BLINKIT_ORG_ID,
  delayDiallersEndPoint,
  dialogState,
  HYPERPURE_ORG_ID,
  tableHeadings,
  TEXT,
} from './constants';
import { useMutationCall, useQueryCall } from '../../../../api/react-client';
import { useClient } from '../../../../api/api-client';
import toast from 'react-hot-toast';
import { userModel } from '../../../../api';
import {
  ChevronDown,
  ChevronUp,
  Gauge,
  LoaderCircle,
  SquarePen,
} from 'lucide-react';
import Button from '../../../../components/ComponentsV1/Button';
import SelectV1 from '../../../../components/serverDrivenUIComponents/SelectV1';
import DDTable from './DDTable';

const defaultDialler = 'Default';

// DelayDaillers component
const DelayDaillers = () => {
  const client = useClient();
  const urlParams = new URLSearchParams(window.location.search);
  const paramsOrgId = Number(urlParams.get('org_id'));

  const [delayDaillersData, setDelayDiallersData] = useState([]);
  const [karmaLabels, setKarmaLabels] = useState([]);
  const [karmaScores, setKarmaScores] = useState([]);
  // state to open remove-diallers dialog
  const [openRemoveDiallersDialog, setOpenRemoveDiallersDialog] = useState(
    dialogState.close,
  );
  // state to open edit-diallers modal
  const [openEditDiallersModal, setOpenEditDiallersModal] = useState(
    dialogState.close,
  );
  // state to store org info
  const [orgInfo, setOrgInfo] = useState({
    allowed_orgs: [],
    default_org: {},
  });
  // state to store selected org id
  const [selectedOrg, setSelectedOrg] = useState({});

  // query call to get delay diallers data
  const { isLoading: isDataLoading, refetch: refetchDelayDiallers } =
    useQueryCall(
      { enabled: selectedOrg?.id ? true : false },
      null,
      ['delay_dialler', selectedOrg?.id],
      () =>
        client(`${delayDiallersEndPoint}`, {
          customOrgId: selectedOrg?.id,
        }).then((res) => {
          setDelayDiallersData(res);
        }),
    );

  //mutation for removing delay diallers
  const { mutate: RemoveDiallers, isLoading: isRemovingDiallers } =
    useMutationCall(
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? TEXT?.DD_REMOVED_SUCCESSFULLY);
          refetchDelayDiallers();
        },
      },
      `${delayDiallersEndPoint}?action=remove`,
      ['remove_delay_dialler', selectedOrg?.id],
      {
        doNotShowSuccessToast: true,
      },
      () =>
        client(`${delayDiallersEndPoint}?action=remove`, {
          customOrgId: selectedOrg?.id,
          data: {},
        }),
    );

  const confirmRemovingDiallers = () => {
    setOpenRemoveDiallersDialog(dialogState.open);
  };

  // Function to handle edit diallers
  const handleEditDiallers = () => {
    setOpenEditDiallersModal(dialogState.open);
  };

  // Function to remove dialler data
  const removeDiallers = () => {
    RemoveDiallers();
    setOpenRemoveDiallersDialog(dialogState.close);
  };

  const getTableRows = (diallersData) => {
    // initialize rowsData array
    const rowsData = [];

    // Loop through Karma labels (Gold, Silver, ...) and get dialler details for each label
    karmaLabels?.forEach((label) => {
      let labelGroupsList = {};

      diallersData.forEach((dialler) => {
        const karmaScore = Object.keys(dialler)[0]; // karma score
        const diallerData = dialler[karmaScore];
        const updated_at = new Date(diallerData?.created_at).toLocaleString();
        const karmaString = diallerData?.karma_details?.karma;

        if (karmaString.includes(label)) {
          if (labelGroupsList[updated_at]) {
            labelGroupsList[updated_at] = [
              ...labelGroupsList[updated_at],
              diallerData,
            ];
          } else {
            labelGroupsList[updated_at] = [diallerData];
          }
        }
      });

      // Now process each group of labels with same updated_at basis their eta values
      Object.entries(labelGroupsList).forEach(
        ([updated_at, karmaDetailsArray]) => {
          // Group karmaDetailsArray by eta_buffer_in_mins
          const etaGroupedMap = new Map();

          karmaDetailsArray?.forEach((karmaDetails) => {
            const currentEtaValue =
              karmaDetails?.delay_diallers?.eta_buffer_in_mins;

            const defaultEtaValue =
              karmaDetails?.default_buffer?.eta_buffer_in_mins;

            const currentEta =
              currentEtaValue != null ? Number(currentEtaValue) : null;

            const defaultEta =
              defaultEtaValue != null ? Number(defaultEtaValue) : null;

            const compositeKey = `${currentEta}_${defaultEta}`;

            if (!etaGroupedMap.has(compositeKey)) {
              etaGroupedMap.set(compositeKey, []);
            }
            etaGroupedMap.get(compositeKey).push(karmaDetails);
          });

          // Create rows based on the grouped ETA values
          etaGroupedMap.forEach((groupedKarmaDetails, compositeKey) => {
            if (groupedKarmaDetails.length === 0) return;

            const [currentEta, defaultEta] = compositeKey.split('_');

            // Check if the dialler is a default dialler
            const isDefaultDialler =
              groupedKarmaDetails?.[0]?.created_by === defaultDialler;
            const rowObj = {
              1: {
                title: (
                  <div className='tw-flex tw-gap-2 tw-flex-wrap'>
                    {groupedKarmaDetails?.map((karmaDetails, index) => (
                      <Label
                        key={`${karmaDetails?.created_at} ${index}`}
                        title={karmaDetails?.karma_details?.karma}
                        bgColor={karmaDetails?.karma_details?.karma_btn_color}
                        textColor={karmaDetails?.karma_details?.karma_txt_color}
                        fontWeight='500'
                        className='!tw-min-w-[75px] !tw-py-2 !tw-text-center tw-flex tw-items-center tw-justify-center'
                      />
                    ))}
                  </div>
                ),
              },
              2: {
                title:
                  currentEta === 'null' || currentEta === null
                    ? '-'
                    : currentEta,
              },
              3: {
                title: isDefaultDialler
                  ? '-'
                  : new Date(
                      groupedKarmaDetails?.[0]?.expiry_ts,
                    ).toLocaleString(),
              },
              4: {
                title: isDefaultDialler
                  ? 'System'
                  : groupedKarmaDetails?.[0]?.created_by,
              },
              5: {
                title: isDefaultDialler ? '-' : updated_at,
              },
              6: {
                title:
                  defaultEta !== null &&
                  defaultEta !== undefined &&
                  defaultEta !== 'null'
                    ? defaultEta
                    : '-',
              },
            };

            rowsData.push(rowObj);
          });
        },
      );
    });

    return rowsData;
  };

  const handleOrgSelect = (_, value) => {
    setSelectedOrg({
      id: value,
      name: orgInfo?.allowed_orgs?.find((Org) => Org.id == value)?.name,
    });
    const url = new URL(window.location.href);
    url.searchParams.set('org_id', value);
    window.history.replaceState({}, '', url);
  };

  // useEffect to extract Karma Labels and Karma Scores
  useEffect(() => {
    if (delayDaillersData && delayDaillersData.length > 0) {
      // setting Karma Labels
      const karmaLabels = new Set();
      delayDaillersData.forEach((item) => {
        const karmaString = Object.values(item)?.[0]?.karma_details?.karma;
        karmaLabels.add(karmaString.split(' ')[0]);
      });
      const karmaLabelsArray = [...karmaLabels].reverse();
      setKarmaLabels(karmaLabelsArray);

      // setting Karma Scores
      const karmaScores = delayDaillersData.reduce((acc, dataItem) => {
        const scoreObject = {
          [Object.entries(dataItem)[0]?.[0]]:
            Object.entries(dataItem)[0]?.[1]?.karma_details?.karma,
        };
        return { ...acc, ...scoreObject };
      }, {});
      setKarmaScores(karmaScores);
    } else return;
  }, [delayDaillersData]);

  // useEffect to get user role info
  useEffect(() => {
    userModel?.userGetRoleInfo({})?.then((response) => {
      if (!response || response?.isError) return null;

      setOrgInfo({
        allowed_orgs: response?.allowed_orgs,
        default_org: response?.default_org,
      });

      // boolean flag to check for valid org ID
      let notValidOrgParamID =
        !paramsOrgId ||
        paramsOrgId === HYPERPURE_ORG_ID ||
        !response?.allowed_orgs?.some((org) => org?.id === paramsOrgId);

      if (notValidOrgParamID) {
        setSelectedOrg(response?.default_org);
        // set the org id in the url if not present
        const url = new URL(window.location.href);
        url.searchParams.set('org_id', response?.default_org?.id);
        window.history.replaceState({}, '', url);
      } else {
        setSelectedOrg({
          id: paramsOrgId,
          name: response?.allowed_orgs?.find((Org) => Org.id == paramsOrgId)
            ?.name,
        });
      }
    });
  }, []);

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-5 tw-p-[2rem] tw-rounded-[1rem] tw-relative tw-bg-white'>
      <div className='tw-w-full tw-justify-between'>
        <div className='tw-text-[2.2rem] tw-font-medium tw-text-zinc-700/95 tw-leading- tw-flex tw-items-center'>
          Delay Diallers Dashboard
          <Gauge className='tw-ml-3 tw-mt-[1px]' size={23} strokeWidth={2.3} />
        </div>
      </div>
      <div className='tw-mt-6 tw-flex-col'>
        <div className='tw-flex tw-justify-between tw-items-end tw-gap-4 tw-mb-4'>
          {/* Org Dropdown */}
          <div className='tw-w-52 tw-h-14'>
            <SelectV1
              selectOptions={orgInfo?.allowed_orgs.filter(
                (org) => org.id !== HYPERPURE_ORG_ID,
              )}
              defaultValue={selectedOrg?.name}
              disabled={false}
              inputKey={'id'}
              saveOptions={handleOrgSelect}
              isMultiSelect={false}
              UparrowIcon={<ChevronUp size={16} />}
              DownarrowIcon={<ChevronDown size={16} />}
              className={`tw-h-full !tw-rounded-xl !tw-border-[1px] tw-shadow-sm ${
                selectedOrg?.id === BLINKIT_ORG_ID
                  ? '!tw-border-yellow-300/80 !tw-bg-yellow-200/10'
                  : selectedOrg?.id === BISTRO_ORG_ID
                  ? '!tw-border-green-300/80 !tw-bg-green-200/10'
                  : '!tw-border-grey-500'
              } `}
              top='!tw-top-[40px]'
            />
          </div>

          {/* Button Group */}
          <div className='tw-flex tw-h-full tw-gap-3'>
            <Button
              onClick={handleEditDiallers}
              title={
                <div className='tw-flex tw-items-center tw-whitespace-nowrap tw-gap-3'>
                  <SquarePen size={15} />
                  Edit Delay Diallers
                </div>
              }
            />
            <Button
              onClick={confirmRemovingDiallers}
              title={'Set Default'}
              type='link'
              className='tw-font-medium hover:tw-bg-grey-100 tw-duration-200 tw-ease-in-out tw-transition-all'
            />
          </div>
        </div>

        {/* Delay Diallers Table */}
        <DDTable
          tableHeadings={tableHeadings}
          isDataLoading={isDataLoading}
          isRemovingDiallers={isRemovingDiallers}
          delayDaillersData={getTableRows(delayDaillersData || [])}
        />

        {/* Loading Spinner */}
        {(isDataLoading || isRemovingDiallers) && (
          <div className='tw-w-full tw-flex tw-items-center tw-justify-center tw-mt-20 tw-text-zinc-500'>
            <LoaderCircle className='tw-animate-spin' />
          </div>
        )}
      </div>

      {openEditDiallersModal && (
        <EditDDModal
          openEditDiallersModal={openEditDiallersModal}
          setOpenEditDiallersModal={setOpenEditDiallersModal}
          KarmaScores={karmaScores}
          refetchDelayDiallers={refetchDelayDiallers}
          selectedOrg={selectedOrg}
        />
      )}

      {openRemoveDiallersDialog && (
        <RemoveDDModal
          openRemoveDiallersDialog={openRemoveDiallersDialog}
          setOpenRemoveDiallersDialog={setOpenRemoveDiallersDialog}
          removeDiallers={removeDiallers}
          selectedOrg={selectedOrg}
        />
      )}
    </div>
  );
};

export default DelayDaillers;
